import React, { useState, useEffect } from 'react'
import {
  IonButton,
  IonButtons,
  IonCol,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonText,
  IonToolbar,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import PopupComponent from '../../../../components/infoPopup/Popup.component'
import { IPatientConflictField } from '../../store/pulsara.types'
import './PatientConflict.component.css'

interface ISelectionPopupProps {
  isOpen: boolean
  field: IPatientConflictField | null
  selectedSource: 'pulsara' | 'emsCharts' | null
  isRequired: boolean
  onDone: (source: 'pulsara' | 'emsCharts' | null) => void
  onCancel: () => void
}

const SelectionPopup: React.FC<ISelectionPopupProps> = ({
  isOpen,
  field,
  selectedSource: initialSource,
  isRequired,
  onDone,
  onCancel,
}) => {
  const { t } = useTranslation()
  const [selectedSource, setSelectedSource] = useState<'pulsara' | 'emsCharts' | null>(null)

  const hasEmsChartsValue = !!field?.emsChartsValue
  const hasPulsaraValue = !!field?.pulsaraValue

  useEffect(() => {
    if (isOpen) {
      setSelectedSource(initialSource)
    }
  }, [isOpen])

  const handleRowClick = (source: 'pulsara' | 'emsCharts', value: string) => {
    if (!value) return

    if (!isRequired && selectedSource === source) {
      // Checkbox behavior
      setSelectedSource(null)
    } else {
      // Radio behavior
      setSelectedSource(source)
    }
  }

  if (!field) return null

  return (
    <PopupComponent
      id='popup-container'
      dataTestId='cc-selection-popup'
      isOpen={isOpen}
      handleHidePopover={onCancel}
      className='info-popup cc-selection-popup'>
      <>
        <IonLabel className='mb-10'>
          <h2 className='cc-popup-title'>{t('pulsara.selectionPopup.chiefComplaint')}</h2>
          <IonText className='cc-popup-subtitle'>{t('pulsara.selectionPopup.selectDataPoint')}</IonText>
        </IonLabel>
        <IonRadioGroup className='custom-radio-group' value={selectedSource}>
          <IonRow
            onClick={() => handleRowClick('emsCharts', field.emsChartsValue)}
            className={`${selectedSource === 'emsCharts' ? 'selected' : ''} ${!hasEmsChartsValue ? 'disabled' : ''}`}>
            <IonCol className='radio-row' size='12'>
              <IonRadio value='emsCharts' aria-label='emsCharts' disabled={!hasEmsChartsValue} />
              <IonLabel>{t('pulsara.patientConflict.emsCharts')}</IonLabel>
            </IonCol>
            <IonCol className='ion-no-padding cc-option-value' size='12'>
              {hasEmsChartsValue ? field.emsChartsValue : t('pulsara.NotAvailable')}
            </IonCol>
          </IonRow>
          <IonRow
            onClick={() => handleRowClick('pulsara', field.pulsaraValue)}
            className={`${selectedSource === 'pulsara' ? 'selected' : ''} ${!hasPulsaraValue ? 'disabled' : ''}`}>
            <IonCol className='radio-row' size='12'>
              <IonRadio value='pulsara' aria-label='pulsara' disabled={!hasPulsaraValue} />
              <IonLabel>{t('pulsara.patientConflict.pulsara')}</IonLabel>
            </IonCol>
            <IonCol className='ion-no-padding cc-option-value' size='12'>
              {hasPulsaraValue ? field.pulsaraValue : t('pulsara.NotAvailable')}
            </IonCol>
          </IonRow>
        </IonRadioGroup>
        <IonToolbar color='light' className=' ion-no-padding'>
          <IonButtons>
            <IonButton
              data-testid='CancelEmail'
              className='btn btn-secondary btn-standard mr-8'
              expand='block'
              onClick={onCancel}>
              {t('common.buttons.Cancel')}
            </IonButton>
            <IonButton
              data-testid='SaveEmail'
              className='btn btn-standard btn-primary'
              expand='block'
              onClick={() => onDone(selectedSource)}>
              {t('common.buttons.Done')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </>
    </PopupComponent>
  )
}

export default SelectionPopup
